"use client";

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Feature {
  id: string;
  icon: string;
  title: string;
  description: string;
  color: string;
  bgColor: string;
}

const CoreFeaturesSection = () => {
  const [visibleCards, setVisibleCards] = useState<number[]>([]);
  const sectionRef = useRef<HTMLDivElement>(null);

  const features: Feature[] = [
    {
      id: 'feature_memorial',
      icon: 'PhotoIcon',
      title: 'Life Story Pages',
      description: 'Build a beautiful personal page with photos, your life story in your own words, favourite music, and cherished memories all in one place.',
      color: 'text-teal-600',
      bgColor: 'bg-teal-100',
    },
    {
      id: 'feature_messages',
      icon: 'ChatBubbleLeftRightIcon',
      title: 'Personal Messages',
      description: 'Write letters, record videos, and leave audio messages that capture your voice, your wisdom, and your love for the people who matter most.',
      color: 'text-amber-700',
      bgColor: 'bg-amber-100',
    },
    {
      id: 'feature_delivery',
      icon: 'CalendarDaysIcon',
      title: 'Scheduled Delivery',
      description: 'Choose specific dates, life events, or upon passing. Your messages arrive exactly when you intend.',
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
    },
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.getAttribute('data-index') || '0');
            setVisibleCards((prev) => (prev.includes(index) ? prev : [...prev, index]));
          }
        });
      },
      { threshold: 0.2 }
    );

    const cardElements = sectionRef.current?.querySelectorAll('.feature-card');
    cardElements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section id="core-features" ref={sectionRef} className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-20">
          <h2 className="font-jakarta font-bold text-4xl md:text-5xl text-foreground mb-6 tracking-tight">
            Everything You Need to Share Your Story
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto leading-relaxed">
            Simple, powerful tools so you can focus on what matters — telling your story and connecting with the people you love.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={feature.id}
              data-index={index}
              className={`feature-card bg-card rounded-3xl border border-border p-8 hover:shadow-2xl transition-all duration-500 ${
                visibleCards.includes(index)
                  ? 'opacity-100 translate-y-0' :'opacity-0 translate-y-12'
              }`}
              style={{ transitionDelay: `${index * 150}ms` }}
            >
              {/* Icon */}
              <div className={`w-16 h-16 ${feature.bgColor} rounded-2xl flex items-center justify-center mb-6`}>
                <Icon name={feature.icon as any} size={32} className={feature.color} />
              </div>

              {/* Content */}
              <h3 className="font-jakarta font-bold text-2xl text-foreground mb-4">{feature.title}</h3>
              <p className="text-muted-foreground leading-relaxed">{feature.description}</p>

              {/* Learn More Link */}
              <a
                href={`/old-landing-page#${feature.id}`}
                className="inline-flex items-center gap-2 mt-6 text-primary hover:text-primary/80 font-semibold transition-colors group"
              >
                Learn more
                <Icon
                  name="ArrowRightIcon"
                  size={16}
                  className="group-hover:translate-x-1 transition-transform"
                />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default CoreFeaturesSection;